import React, { useState, useEffect, useId, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { FiEdit3 } from 'react-icons/fi';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import CrmModalShell from './CrmModalShell';
import LoadingButton from './LoadingButton';
import {
  CRM_TYPE_CONFIG as TYPE_CONFIG,
  CRM_STATUS_CONFIG as STATUS_CONFIG,
  crmDateKey as dateKey,
} from './CrmTimeline';
import {
  CRM_TRACKABLE_TYPES,
  isTrackableCrmType,
  defaultDurationForType,
} from '../config/crmServiceTracking';
import { queryKeys } from '../config/queryKeys';
import '../pages/CRM.css';
import { appAlert } from '../utils/appDialog';

const VALID_STATUSES = Object.keys(STATUS_CONFIG);

export function emptyCrmInteractionForm(candidateId = '') {
  return {
    candidate_id: candidateId ? String(candidateId) : '',
    interaction_type: 'call',
    interaction_date: new Date().toISOString().split('T')[0],
    notes: '',
    follow_up_date: '',
    status: 'open',
    duration_minutes: '',
    track_services: true,
    service_enrollment_id: '',
  };
}

function interactionToForm(row) {
  const typeKey = row.interaction_type && TYPE_CONFIG[row.interaction_type] ? row.interaction_type : 'note';
  return {
    candidate_id: row.candidate_id != null ? String(row.candidate_id) : '',
    interaction_type: typeKey,
    interaction_date: dateKey(row.interaction_date) || new Date().toISOString().split('T')[0],
    notes: row.notes || '',
    follow_up_date: row.follow_up_date ? dateKey(row.follow_up_date) : '',
    status: VALID_STATUSES.includes(row.status) ? row.status : 'open',
    duration_minutes: row.duration_minutes != null ? String(row.duration_minutes) : '',
    track_services: row.track_services === 0 || row.track_services === false ? false : true,
    service_enrollment_id: row.service_enrollment_id ? String(row.service_enrollment_id) : '',
  };
}

function serviceSyncMessage(serviceSync) {
  if (!serviceSync) return null;
  if (serviceSync.synced) {
    const unit =
      serviceSync.catalog_code === 'consultant_minute'
        ? `${serviceSync.quantity} min`
        : `${serviceSync.quantity} session(s)`;
    const payout =
      Number(serviceSync.payout_amount) > 0
        ? ` Consultant payout: $${Number(serviceSync.payout_amount).toFixed(2)}${serviceSync.payout_status ? ` (${serviceSync.payout_status})` : ''}.`
        : '';
    return `Service package updated (${unit}).${payout}`;
  }
  if (serviceSync.reason && !serviceSync.reason.includes('not linked')) {
    return `CRM saved. Services: ${serviceSync.reason}`;
  }
  return null;
}

const CrmInteractionModal = ({
  open,
  onClose,
  interaction = null,
  candidateId = '',
  lockCandidate = false,
  candidateLabel = '',
  fallbackCandidateLabel = '',
}) => {
  const fieldId = useId().replace(/:/g, '');
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState(emptyCrmInteractionForm());

  const canWrite = user?.role === 'consultant' || user?.role === 'admin';
  const isEditing = Boolean(interaction?.id);
  const trackable = isTrackableCrmType(formData.interaction_type);
  const trackConfig = CRM_TRACKABLE_TYPES[formData.interaction_type];

  const { data: candidates } = useQuery(
    ['crm-candidates', user?.role],
    () =>
      user?.role === 'admin'
        ? api.get('/candidates').then((res) => res.data)
        : api.get('/candidates/assigned').then((res) => res.data),
    { enabled: open && canWrite && !lockCandidate }
  );

  const selectedCandidateId = formData.candidate_id || (lockCandidate ? String(candidateId) : '');

  const { data: enrollments } = useQuery(
    ['service-enrollments', selectedCandidateId],
    () => api.get(`/services/candidates/${selectedCandidateId}/enrollments`).then((res) => res.data),
    { enabled: open && Boolean(selectedCandidateId) }
  );

  const activeEnrollments = useMemo(
    () => (enrollments || []).filter((e) => e.status === 'active' || e.status === 'on_hold'),
    [enrollments]
  );

  useEffect(() => {
    if (!open) return;
    if (interaction) {
      setFormData(interactionToForm(interaction));
      return;
    }
    setFormData(emptyCrmInteractionForm(lockCandidate ? candidateId : candidateId || ''));
  }, [open, interaction, candidateId, lockCandidate]);

  useEffect(() => {
    if (!open || !trackable || formData.duration_minutes) return;
    const defaultDuration = defaultDurationForType(formData.interaction_type);
    if (defaultDuration) {
      setFormData((prev) => ({ ...prev, duration_minutes: String(defaultDuration) }));
    }
  }, [open, formData.interaction_type, trackable, formData.duration_minutes]);

  useEffect(() => {
    if (!open || !activeEnrollments.length) return;
    setFormData((prev) => {
      if (prev.service_enrollment_id) return prev;
      return { ...prev, service_enrollment_id: String(activeEnrollments[0].id) };
    });
  }, [open, activeEnrollments]);

  const invalidateAll = () => {
    queryClient.invalidateQueries(queryKeys.crmRoot);
    queryClient.invalidateQueries(['candidate']);
    queryClient.invalidateQueries(queryKeys.serviceEnrollments);
    queryClient.invalidateQueries(queryKeys.serviceActivities);
    queryClient.invalidateQueries('service-finance-summary');
    queryClient.invalidateQueries(queryKeys.financeLedger());
    queryClient.invalidateQueries('service-work-summary');
  };

  const createMutation = useMutation((data) => api.post('/crm', data), {
    onSuccess: (res) => {
      invalidateAll();
      const msg = serviceSyncMessage(res.data?.service_sync);
      if (msg) void appAlert(msg);
      onClose?.();
    },
    onError: (e) => void appAlert(e.response?.data?.error || e.message),
  });

  const updateMutation = useMutation(({ id, data }) => api.put(`/crm/${id}`, data), {
    onSuccess: (res) => {
      invalidateAll();
      const msg = serviceSyncMessage(res.data?.service_sync);
      if (msg) void appAlert(msg);
      onClose?.();
    },
    onError: (e) => void appAlert(e.response?.data?.error || e.message),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      candidate_id: parseInt(formData.candidate_id, 10),
      interaction_type: formData.interaction_type,
      interaction_date: formData.interaction_date,
      notes: formData.notes || null,
      follow_up_date: formData.follow_up_date || null,
      status: formData.status,
      duration_minutes: formData.duration_minutes ? parseInt(formData.duration_minutes, 10) : null,
      track_services: trackable ? formData.track_services : false,
      service_enrollment_id:
        trackable && formData.track_services && formData.service_enrollment_id
          ? parseInt(formData.service_enrollment_id, 10)
          : null,
    };
    if (isEditing) {
      updateMutation.mutate({ id: interaction.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const saving = createMutation.isLoading || updateMutation.isLoading;
  const lockedLabel =
    candidateLabel ||
    fallbackCandidateLabel ||
    (candidateId ? `Candidate #${candidateId}` : '');

  if (!canWrite) return null;

  const formDomId = `${fieldId}-crm-form`;

  return (
    <CrmModalShell
      open={open}
      onClose={onClose}
      icon={FiEdit3}
      title={isEditing ? 'Edit interaction' : 'Log interaction'}
      subtitle={
        isEditing
          ? 'Update this CRM touchpoint and optional service tracking.'
          : 'Record a call, meeting, or note with your candidate.'
      }
      ariaLabel={isEditing ? 'Edit interaction' : 'Log interaction'}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <LoadingButton
            type="submit"
            form={formDomId}
            className="btn btn-primary"
            icon={FiEdit3}
            loading={saving}
            loadingLabel="Saving…"
          >
            {isEditing ? 'Save changes' : 'Create'}
          </LoadingButton>
        </>
      }
    >
      <form id={formDomId} onSubmit={handleSubmit} className="crm-modal-inline-form">
        <div className="form-group">
          <label htmlFor={`${fieldId}-candidate`}>Candidate</label>
          {lockCandidate ? (
            <div className="crm-modal-candidate-lock" id={`${fieldId}-candidate`}>
              {lockedLabel}
            </div>
          ) : (
            <select
              id={`${fieldId}-candidate`}
              value={formData.candidate_id}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  candidate_id: e.target.value,
                  service_enrollment_id: '',
                })
              }
              required
            >
              <option value="">Select candidate</option>
              {formData.candidate_id &&
                !candidates?.some((c) => String(c.id) === String(formData.candidate_id)) && (
                  <option value={formData.candidate_id}>
                    {fallbackCandidateLabel || `Candidate #${formData.candidate_id}`}
                  </option>
                )}
              {candidates?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.first_name} {c.last_name}
                  {c.email ? ` (${c.email})` : ''}
                </option>
              ))}
            </select>
          )}
        </div>
        <div className="form-group">
          <label htmlFor={`${fieldId}-type`}>Interaction type</label>
          <select
            id={`${fieldId}-type`}
            value={formData.interaction_type}
            onChange={(e) => {
              const nextType = e.target.value;
              const defaultDuration = defaultDurationForType(nextType);
              setFormData({
                ...formData,
                interaction_type: nextType,
                duration_minutes: defaultDuration ? String(defaultDuration) : '',
              });
            }}
            required
          >
            {Object.entries(TYPE_CONFIG).map(([k, { label }]) => (
              <option key={k} value={k}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor={`${fieldId}-date`}>Date</label>
            <input
              id={`${fieldId}-date`}
              type="date"
              value={formData.interaction_date}
              onChange={(e) => setFormData({ ...formData, interaction_date: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor={`${fieldId}-status`}>Status</label>
            <select
              id={`${fieldId}-status`}
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            >
              {Object.entries(STATUS_CONFIG).map(([k, { label }]) => (
                <option key={k} value={k}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        </div>
        {trackable && trackConfig?.usesDuration && (
          <div className="form-group">
            <label htmlFor={`${fieldId}-duration`}>Duration (minutes)</label>
            <input
              id={`${fieldId}-duration`}
              type="number"
              min={1}
              step={1}
              value={formData.duration_minutes}
              onChange={(e) => setFormData({ ...formData, duration_minutes: e.target.value })}
              placeholder={`Default ${trackConfig.defaultDuration} min`}
            />
          </div>
        )}
        {trackable && (
          <div className="form-group crm-service-tracking">
            <label className="crm-checkbox-label">
              <input
                type="checkbox"
                checked={formData.track_services}
                onChange={(e) => setFormData({ ...formData, track_services: e.target.checked })}
              />
              Track against service package ({trackConfig.label})
            </label>
            {formData.track_services && activeEnrollments.length > 1 && (
              <select
                className="crm-enrollment-select"
                value={formData.service_enrollment_id}
                onChange={(e) => setFormData({ ...formData, service_enrollment_id: e.target.value })}
              >
                {activeEnrollments.map((enrollment) => (
                  <option key={enrollment.id} value={enrollment.id}>
                    {enrollment.package_name} — {enrollment.company_name}
                  </option>
                ))}
              </select>
            )}
            {formData.track_services && formData.status === 'completed' && !activeEnrollments.length && (
              <p className="crm-service-hint">No active service enrollment for this candidate.</p>
            )}
            {formData.track_services && formData.status !== 'completed' && (
              <p className="crm-service-hint">
                Set status to Completed to deduct from the service package.
              </p>
            )}
          </div>
        )}
        <div className="form-group">
          <label htmlFor={`${fieldId}-notes`}>Notes</label>
          <textarea
            id={`${fieldId}-notes`}
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            rows={5}
            placeholder="Outcomes, next steps, objections…"
          />
        </div>
        <div className="form-group">
          <label htmlFor={`${fieldId}-follow`}>Follow-up date (optional)</label>
          <input
            id={`${fieldId}-follow`}
            type="date"
            value={formData.follow_up_date}
            onChange={(e) => setFormData({ ...formData, follow_up_date: e.target.value })}
          />
        </div>
      </form>
    </CrmModalShell>
  );
};

export default CrmInteractionModal;
